import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { memberApi, memberTokenStore } from "../lib/memberApi";
import { formatApiErrorDetail } from "../lib/api";

const MemberContext = createContext(null);

// State semantics: null = checking, false = anonymous, object = authenticated member.
export function MemberProvider({ children }) {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip the /me check while an OAuth redirect is still being processed —
    // MemberAuthCallback will exchange the session_id and set the token first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!memberTokenStore.get()) {
      setMember(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await memberApi.get("/member/auth/me");
      setMember(data);
    } catch (_err) {
      memberTokenStore.clear();
      setMember(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = useCallback(() => {
    const redirectUrl = window.location.origin + "/member";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchangeSession = useCallback(async (sessionId) => {
    try {
      const { data } = await memberApi.post("/member/auth/session", { session_id: sessionId });
      if (data.session_token) memberTokenStore.set(data.session_token);
      setMember(data);
      return { ok: true, member: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await memberApi.post("/member/auth/logout");
    } catch (e) {
      console.warn("Member logout API call failed:", e?.message);
    }
    memberTokenStore.clear();
    setMember(false);
  }, []);

  const subscribe = useCallback(async () => {
    try {
      const { data } = await memberApi.post("/member/subscribe", { origin_url: window.location.origin });
      window.location.href = data.url;
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const refreshSubscriptionStatus = useCallback(async (sessionId) => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      const { data } = await memberApi.get("/member/subscription/status", { params });
      setMember((m) => (m ? { ...m, subscription_status: data.subscription_status } : m));
      return data.subscription_status;
    } catch (e) {
      return null;
    }
  }, []);

  return (
    <MemberContext.Provider
      value={{ member, loading, login, exchangeSession, logout, subscribe, refreshSubscriptionStatus, refresh: checkAuth }}
    >
      {children}
    </MemberContext.Provider>
  );
}

export const useMember = () => useContext(MemberContext);
